import { Injectable } from '@angular/core';
import { MoneyFundLiquidity } from '@products/models/money-fund-liquidity';
import { MoneyFundLiquidityService } from '@products/services/money-fund-liquidity.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  LiquidityState,
  ProductDetailConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import { getHighchartsLocaleFormat } from '@utils/text/date-utils';
import { Subject } from 'rxjs';
import liquidAssetsData from '@graphql/pricing-distribution/liquid-assets.graphql';
import shadowNavData from '@graphql/pricing-distribution/shadow-nav.graphql';
import dayjs, { Dayjs } from 'dayjs';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import { SeriesOptionsType } from 'highcharts';
import { toLocaleNumber } from '@utils/il8n/number-il8n';
import { LIQUIDITY_INITIAL_STATE } from './pricing-distribution.config';
import { symbols } from '@utils/il8n/currency';

const logger = Logger.getLogger('LiquidityService');

interface ChartLabels {
  dailyLiquidAssetsLabel: string;
  weeklyLiquidAssetsLabel: string;
  shadowNavLabel: string;
}

type CustomSeriesOptions = SeriesOptionsType & {
  min: number;
  currencySymbol?: string;
};

enum ComponentType {
  'LA' = 'Liquid Assets',
  'HSN' = 'Historical Shadow Nav',
}

enum Timeline {
  'DAILY',
  'WEEKLY',
}

@Injectable()
export class LiquidityService {
  private state: LiquidityState;
  private configurationName: ConfigurationId;
  private componentType: ComponentType;
  liquidityState: Subject<LiquidityState>;

  constructor(
    private moneyFundLiquidityService: MoneyFundLiquidityService,
    private translateService: TranslateService
  ) {
    this.state = cloneDeep(LIQUIDITY_INITIAL_STATE);
    this.liquidityState = new Subject();
  }

  /**
   * Call register method of money fund liquidity service.
   * @param productDetailConfiguration the configuration received on page detail load
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    this.componentType = (productDetailConfiguration.componentType as unknown) as ComponentType;

    this.moneyFundLiquidityService
      .register(
        this.componentType === ComponentType.LA
          ? liquidAssetsData
          : shadowNavData,
        {
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }
      )
      .subscribe((liquidities) => {
        logger.debug(liquidities);
        this.mapState(liquidities);
      });
  }

  /**
   * Map the money fund liquidties to the state accordingly.
   * @param liquidities feed from the service.
   */
  mapState(liquidities: MoneyFundLiquidity[]): void {
    this.state.historicalData = liquidities;

    if (liquidities?.length) {
      this.state.chartConfig.highChartOptions = this.fetchChartOptions();
      this.createChart(this.state.chartConfig.selectedToggleRange);

      this.state.hasLoadedFullFeed = true;
    }

    this.liquidityState.next(this.state);
  }

  setConfigurationName(configurationName: ConfigurationId): void {
    this.configurationName = configurationName;
  }

  getConfigurationName(): ConfigurationId {
    return this.configurationName;
  }

  /**
   * Fetch initial chart options.
   */
  fetchChartOptions(): Highcharts.Options {
    return {
      title: {
        text: undefined,
      },
      chart: {
        spacingLeft: 0,
        spacingRight: 0,
        marginRight: 20,
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      xAxis: {
        type: 'datetime',
        gridLineDashStyle: 'LongDash',
        // COREWEB-2921 increase tickPixelInterval from default 100, to force extra width for labels
        // NB: new value found by trial and error, and doesn't seem to reflect the actual pixel width
        tickPixelInterval: 110,
        // WORKAROUND: COREWEB-1543 set hour/minute/second to day display
        dateTimeLabelFormats: {
          millisecond: '%e. %b',
          second: '%e. %b',
          minute: '%e. %b',
          hour: '%e. %b',
        },
      },
      yAxis: [
        {
          gridLineDashStyle: 'LongDash',
          labels: {
            format:
              this.componentType === ComponentType.LA
                ? '{value}%'
                : this.getCurrencySymbol(
                    this.state.historicalData[0].currencyCode
                  ) + '{value}',
          },
          title: {
            text: null,
          },
          minorTickInterval: 'auto',
        },
      ],
      legend: {
        enabled: true,
        verticalAlign: 'bottom',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        // COREWEB-1277 set to day precision
        // http://api.highcharts.com/highcharts/tooltip.dateTimeLabelFormats
        xDateFormat: getHighchartsLocaleFormat(),
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
        },
      },
    };
  }

  /**
   * Return the currency code as special character against alphabet format.
   * @param currencyCode the currency code in three character string format (alphabet).
   */
  getCurrencySymbol(currencyCode: string): string {
    return symbols.get(currencyCode);
  }

  /**
   * Filter the historical liquidities chart data as per the toggle range selected.
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  createChart(
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): void {
    const {
      filteredLiquidities,
      fromDateStd,
      toDateStd,
    } = this.getFilteredDataByDateRange(
      this.state.historicalData,
      selectedToggleRange,
      fromDate,
      toDate
    );

    if (this.componentType === ComponentType.LA) {
      this.state.chartConfig.chartTitle = this.translateService.instant(
        'products.price-liquid-assets-chart-header'
      );
      this.state.chartConfig.proximalPlacementTop =
        'liquidAssets.ChartProximalTop';
      this.state.chartConfig.proximalPlacementBottom =
        'liquidAssets.ChartProximalBottom';
      this.state.chartConfig.caveatPlacement = 'liquidAssets.ChartCaveat';
      this.state.chartConfig.tooltip = this.translateService.tooltip(
        'products.price-liquid-assets-chart-header'
      );
    } else {
      this.state.chartConfig.chartTitle = this.translateService.instant(
        'products.price-shadow-nav-chart-header'
      );
      this.state.chartConfig.proximalPlacementTop =
        'shadowNav.ChartProximalTop';
      this.state.chartConfig.proximalPlacementBottom =
        'shadowNav.ChartProximalBottom';
      this.state.chartConfig.caveatPlacement = 'shadowNav.ChartCaveat';
      this.state.chartConfig.tooltip = this.translateService.tooltip(
        'products.price-shadow-nav-chart-header'
      );
    }

    this.state.asOfDate = filteredLiquidities[0].asOfDate;

    const chartLabels = this.getChartLabels();

    this.state.chartConfig.highChartOptions.series = this.fetchChartSeries(
      filteredLiquidities,
      this.state.historicalData[0].currencyCode,
      chartLabels
    );

    // Also, set the date values in state.
    this.state.chartConfig.selectedToggleRange = selectedToggleRange;
    this.state.chartConfig.customRange.fromDateStd = fromDateStd;
    this.state.chartConfig.customRange.toDateStd = toDateStd;

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this.liquidityState.next(this.state);
    }
  }

  /**
   * Filter the historical feed as per the date range, if available, and return accordingly.
   * @param historicalData historical data from feed
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  getFilteredDataByDateRange(
    historicalData: MoneyFundLiquidity[],
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): {
    filteredLiquidities: MoneyFundLiquidity[];
    fromDateStd: string;
    toDateStd: string;
  } {
    let filteredLiquidities: MoneyFundLiquidity[];
    let fromDateStd: string;
    let toDateStd: string;

    if (selectedToggleRange === 'CUSTOM_RANGE' && toDate) {
      toDateStd = dayjs(toDate).format('YYYY-MM-DD');
    } else if (selectedToggleRange === 'LAST_QUARTER') {
      toDateStd = dayjs()
        .subtract(1, 'quarter')
        .endOf('quarter')
        .format('YYYY-MM-DD');
    } else if (selectedToggleRange === 'LAST_CALENDAR_YEAR') {
      toDateStd = dayjs()
        .subtract(1, 'year')
        .endOf('year')
        .format('YYYY-MM-DD');
    } else {
      // Assigned toDateStd to be as of pricing history's first nav date.
      // This will compensate the possibility of last nav date less than today's date.
      toDateStd = dayjs(historicalData[0]?.asOfDateStd).format('YYYY-MM-DD');
    }
    if (selectedToggleRange === 'CUSTOM_RANGE' && fromDate) {
      fromDateStd = this.calculateFromDateStd(
        selectedToggleRange,
        dayjs(fromDate).format('YYYY-MM-DD'),
        toDateStd
      );
    } else {
      fromDateStd = this.calculateFromDateStd(
        selectedToggleRange,
        this.state.historicalData[historicalData.length - 1].asOfDateStd,
        toDateStd
      );
    }

    // LOGIC: Filter the pricing history (original data) from state, as per 'from/to' standard date, by dayjs library.
    filteredLiquidities = historicalData.filter(
      (liquidities: MoneyFundLiquidity) =>
        dayjs(liquidities.asOfDateStd, 'YYYY-MM-DD').isBetween(
          fromDateStd,
          toDateStd,
          null,
          '[]'
        )
    );

    return {
      filteredLiquidities,
      fromDateStd,
      toDateStd,
    };
  }

  /**
   * Calculate from date value in standard format.
   * @param type the key of the DATE_RANGE_ENUM map
   * @param startDateStd start date standard
   * @param endDateStd end date standard
   */
  calculateFromDateStd(
    type: string,
    startDateStd: string,
    endDateStd: string
  ): string {
    const startMoment = dayjs(startDateStd, 'YYYY-MM-DD');
    const endMoment = dayjs(endDateStd, 'YYYY-MM-DD');
    let startOfYear: Dayjs;
    let startOfQtr: Dayjs;

    switch (type) {
      case 'CUSTOM_RANGE':
        // custom range could be anything up to the full inception range
        return startDateStd;
      case 'SINCE_INCEPTION':
        return startDateStd;
      case 'YTD':
        startOfYear = endMoment.startOf('year');
        if (startOfYear.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfYear.format('YYYY-MM-DD');
      case 'LAST_QUARTER':
        startOfQtr = endMoment.startOf('quarter');
        if (startOfQtr.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfQtr.format('YYYY-MM-DD');
      case 'LAST_CALENDAR_YEAR':
        startOfYear = endMoment.startOf('year');
        if (startOfYear.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfYear.format('YYYY-MM-DD');
      default:
        const period = this.state.chartConfig.dateRangeEnum.get(type).period;
        const fromDate = endMoment.subtract(
          period.value as number,
          period.key as dayjs.UnitType
        );
        if (fromDate.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return fromDate.format('YYYY-MM-DD');
    }
  }

  /**
   * Return the chart label-name as an object.
   */
  getChartLabels(): ChartLabels {
    return {
      dailyLiquidAssetsLabel: this.translateService.instant(
        'products.price-liquid-assets-daily'
      ),
      weeklyLiquidAssetsLabel: this.translateService.instant(
        'products.price-liquid-assets-weekly'
      ),
      shadowNavLabel: this.translateService.instant(
        'products.price-shadow-nav'
      ),
    };
  }

  /**
   * Fetch chart series data as per liquidities.
   * @param liquidities pricing history available
   * @param currencyCode currency code in feed
   * @param chartLabels chart labels for calctypes
   */
  fetchChartSeries(
    liquidities: MoneyFundLiquidity[],
    currencyCode: string,
    chartLabels: ChartLabels
  ): SeriesOptionsType[] {
    if (this.componentType === ComponentType.LA) {
      const dailyValues = this.convertHistoryIntoSeries(
        liquidities,
        Timeline.DAILY
      );

      // COREWEB-6246 remove HTML from label
      // we cannot remove whole html, because sometimes label contains html tag
      // we need to remove only tooltip code, which is added automatically in getLabel
      // code is removing <a> tag with its content from label
      // first we need to remove new lines, which are quite often in labels
      // this can be refactored in the future and support for HTML in charts title may be easily added - extensive testing required
      const dailyLabel = chartLabels.dailyLiquidAssetsLabel
        ?.replace(/(\r\n|\n|\r)/gm, '')
        .replace(/<a([^>]+?)([^>]*?)>(.*?)<\/a>/gi, '');

      const dailySeries: CustomSeriesOptions = {
        name: dailyLabel,
        type: 'line',
        min: 0,
        tooltip: {
          pointFormatter: this.pointFormatterProvider(this),
        },
        data: dailyValues,
      };

      let weeklySeries: CustomSeriesOptions = null;
      const weeklyValues = this.convertHistoryIntoSeries(
        liquidities,
        Timeline.WEEKLY
      );

      weeklySeries = {
        name: chartLabels.weeklyLiquidAssetsLabel,
        type: 'line',
        min: 0,
        tooltip: {
          pointFormatter: this.pointFormatterProvider(this),
        },
        data: weeklyValues,
      };

      return [dailySeries, weeklySeries];
    } else {
      const navValues = this.convertHistoryIntoSeries(liquidities);
      const currencySymbol = this.getCurrencySymbol(currencyCode);

      // COREWEB-6246 remove HTML from label
      // we cannot remove whole html, because sometimes label contains html tag
      // we need to remove only tooltip code, which is added automatically in getLabel
      // code is removing <a> tag with its content from label
      // first we need to remove new lines, which are quite often in labels
      // this can be refactored in the future and support for HTML in charts title may be easily added - extensive testing required
      const navLabel = chartLabels.shadowNavLabel
        ?.replace(/(\r\n|\n|\r)/gm, '')
        .replace(/<a([^>]+?)([^>]*?)>(.*?)<\/a>/gi, '');

      const navSeries: CustomSeriesOptions = {
        name: navLabel,
        type: 'line',
        min: 0,
        tooltip: {
          pointFormatter: this.pointFormatterProvider(this),
        },
        data: navValues,
        currencySymbol, // this will be available as this.series.userOptions.currencySymbol in pointFormatterProvider()
      };

      return [navSeries];
    }
  }

  /**
   * Convert daily/weekly liquidities into chart series.
   * @param liquidities historic values
   * @param timeline daily/weekly
   */
  convertHistoryIntoSeries(
    liquidities: MoneyFundLiquidity[],
    timeline?: Timeline
  ) {
    const timelineValues = [];
    const currencySymbol = this.getCurrencySymbol(
      this.state.historicalData[0].currencyCode
    );

    if (liquidities) {
      const timelineHistoryLength = liquidities.length - 1;
      for (let c = timelineHistoryLength; c >= 0; c--) {
        const timelinePoint = liquidities[c];
        let ts = this.formatStdDateToUnixTimeStamp(timelinePoint.asOfDateStd);
        let val: number = null;

        // COREWEB-1539 : edge case 2017-05-05 00:00 being parsed as 2017-05-04 24:00, add a second
        // ps quicker than .set('second', 1)
        ts += 1 * 1000;

        if (this.componentType === ComponentType.LA) {
          switch (timeline) {
            case Timeline.DAILY:
              val = parseFloat(timelinePoint.amortizedDailyLiquidAssetRatio);
              break;
            case Timeline.WEEKLY:
              val = parseFloat(timelinePoint.amortizedWeeklyLiquidAssetRatio);
              break;
          }
        } else {
          val = parseFloat(timelinePoint.shadowNav.replace(currencySymbol, ''));
        }

        if (!isNaN(val)) {
          timelineValues.push([ts, val]);
        }
      }
    }

    return timelineValues;
  }

  /**
   * Highcharts tooltip render function.
   * @param self current class object
   */
  pointFormatterProvider(self: LiquidityService) {
    return function (): any {
      let baseLineNav = parseFloat(this.series.data[0].y);

      // COREWEB-4911 : first point can be invalid
      if (!baseLineNav) {
        const baseLineNavPoint = find(this.series.data, (dataPoint) => {
          return !!dataPoint.y;
        });
        if (baseLineNavPoint) {
          baseLineNav = parseFloat(baseLineNavPoint.y);
        }
      }

      const pointValue = parseFloat(this.y);
      let tooltipHtml: string;

      if (self.componentType === ComponentType.LA) {
        tooltipHtml = `
          <span style='font-size:14px;color:${this.series.color}'>\u25CF</span>
          <span>${this.series.name}: </span>
          <b>${toLocaleNumber(pointValue, 'percentage')}%</b>
        `;

        tooltipHtml = '<br/>' + tooltipHtml;
      } else {
        tooltipHtml = `
          <span style='font-size:14px;color:${this.series.color}'>\u25CF</span>
          <span>${this.series.name}: </span>
          <b>${this.series.userOptions.currencySymbol}${toLocaleNumber(
          pointValue,
          'standard'
        )}</b>
        `;
      }

      return tooltipHtml;
    };
  }

  /**
   * Return UTC standard unix timestamp, when given std format day eg YYYY-MM-DD
   * @param val date in format locale or YYYY-MM-DD
   * @returns UTC unix timestamp
   */
  formatStdDateToUnixTimeStamp(val) {
    if (!val || val === '-') {
      return null;
    }

    const stdDateFormatMatch = val.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);
    if (!stdDateFormatMatch) {
      return null;
    }

    const year = stdDateFormatMatch[1];
    const month = stdDateFormatMatch[2] - 1;
    const day = stdDateFormatMatch[3];
    const date = Date.UTC(year, month, day, 0, 0);

    return date.valueOf();
  }
}
